import { SparklesIcon } from "@heroicons/react/24/outline";

import usePartAiTitle from "../../data/usePartAiTitle";
import useSaveEbayOfferTitle from "../../data/useSaveEbayOfferTitle";
import { IPart } from "../../models/Part";
import classNames from "../../utils/classNames";
import useNotification from "../notifications/useNotifications";
import Tooltip from "../shared/Tooltip";

type Props = {
  part: IPart;
  disabled?: boolean;
  /** When set, AI titles are handed off here instead of writing the live offer. */
  onSaveTitle?: (title: string) => void;
};

/**
 * Compact one-click AI title button intended to sit inline next to the
 * title-builder dropdown toggle. Generates an AI-optimised eBay title and
 * saves it straight onto the part's offer (or via onSaveTitle when staging).
 */
export default function QuickAiTitleButton({ part, disabled = false, onSaveTitle }: Props) {
  const { generateAiTitle } = usePartAiTitle();
  const { saveTitle, isSaving } = useSaveEbayOfferTitle();
  const { addNotification } = useNotification();

  const isBusy = generateAiTitle.isLoading || (!onSaveTitle && isSaving);

  const handleClick = () => {
    generateAiTitle.mutate(part.id, {
      onSuccess: (response) => {
        if (response.success && response.data) {
          if (onSaveTitle) {
            onSaveTitle(response.data.title);
          } else {
            saveTitle(part, response.data.title);
          }
        } else {
          addNotification({
            variant: "error",
            primaryText: response.message ?? "Unable to generate AI title",
          });
        }
      },
      onError: (error: any) => {
        addNotification({
          variant: "error",
          primaryText: error?.message ?? "Unable to generate AI title",
        });
      },
    });
  };

  return (
    <Tooltip
      content={onSaveTitle ? "Generate and stage an AI eBay title" : "Generate and save an AI eBay title"}
      className="flex-none"
    >
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled || isBusy}
        aria-label={onSaveTitle ? "Generate and stage an AI eBay title" : "Generate and save an AI eBay title"}
        className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-violet-300 bg-white text-violet-700 shadow-sm transition hover:border-violet-400 hover:bg-violet-50 hover:text-violet-800 disabled:opacity-60"
      >
        <SparklesIcon
          className={classNames("h-4 w-4", isBusy && "animate-pulse")}
          aria-hidden
        />
      </button>
    </Tooltip>
  );
}
